import FormElement, { Field as BaseField, useForm } from "rc-field-form";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/Button";
import Field from "../../../../../components/Field";
import HourTypeSelect from "../../../../../components/Inputs/HourTypeSelect";
import ResourceSelect from "../../../../../components/Inputs/ResourceSelect";
import Select from "../../../../../components/Inputs/Select";
import { generateErrorsConfigForForm } from "../../../../../utils/helpers/errors";
import {
    getSettingsResourceSearchFilters,
    renderSettingsResourceLabel,
} from "../../../../../utils/helpers/settings";
import { useModuleAccess } from "../../../../../utils/hooks/access";
import { useIsMasterCompany } from "../../../../../utils/hooks/user";
import { useIsFieldDisabled } from "../../../helpers/useIsFieldDisabled";
import {
    getAllocatedResources,
    resourceSelectConfig,
    resourceTypeOptions,
} from "../constants";
import ManageAbilitySelect from "./ManageAbilitySelect";

function Form({ mode, values, error, loading, submit, close }) {
    const { t } = useTranslation();
    const [form] = useForm();
    const isMasterCompany = useIsMasterCompany();
    const { access } = useModuleAccess("settings.rules.allocationRules");
    const disabled = useIsFieldDisabled({ ...access, mode });

    const onClose = useCallback(() => {
        form.resetFields();
        close();
    }, [form, close]);

    const onTypeChange = useCallback(() => {
        form.setFieldsValue({
            payCodes: [],
            earningGroups: [],
            groupings: [],
            hourTypes: [],
        });
    }, [form]);

    const onFinish = useCallback(
        (values) => {
            submit({
                type: values?.type,
                manageAbility: values?.manageAbility,
                status: "active",
                payCodes: Array.isArray(values?.payCodes)
                    ? values.payCodes.map((item) => item?.id)
                    : [],
                earningGroups: Array.isArray(values?.earningGroups)
                    ? values.earningGroups.map((item) => item?.id)
                    : [],
                groupings: Array.isArray(values?.groupings)
                    ? values.groupings.map((item) => item?.id)
                    : [],
                hourTypes: Array.isArray(values?.hourTypes) ? values.hourTypes : [],
                roles: Array.isArray(values?.roles)
                    ? values.roles.map((item) => item?.id)
                    : [],
            });
            close();
        },
        [submit, close],
    );

    useEffect(() => {
        const resourceItems = getAllocatedResources(values);
        form.resetFields();
        form.setFieldsValue({
            type: values?.type,
            payCodes: values?.type === "pay-code" ? resourceItems : [],
            earningGroups: values?.type === "earning-group" ? resourceItems : [],
            groupings: values?.type === "groupings" ? resourceItems : [],
            hourTypes: values?.type === "hour-type" ? resourceItems : [],
            manageAbility: values?.manageAbility,
            roles: Array.isArray(values?.roles)
                ? values.roles
                : values?.role
                    ? [values.role]
                    : [],
        });
    }, [form, values]);

    useEffect(() => {
        const fieldErrors = generateErrorsConfigForForm(
            [
                "type",
                "payCodes",
                "earningGroups",
                "groupings",
                "hourTypes",
                "manageAbility",
                "roles",
            ],
            error,
        );
        form.setFields(fieldErrors);
    }, [error, form]);

    return (
        <FormElement
            form={form}
            onFinish={onFinish}
            className="d-flex flex-column justify-content-between h-100"
        >
            <div>
                <Field
                    name="type"
                    label={t("type")}
                    rules={[
                        {
                            required: true,
                            message: t("required-type"),
                        },
                    ]}
                >
                    <Select
                        placeholder={t("type")}
                        disabled={disabled || !isMasterCompany}
                        onChange={onTypeChange}
                    >
                        {resourceTypeOptions.map(({ value, label }) => (
                            <Select.Option key={value} value={value}>
                                {t(label)}
                            </Select.Option>
                        ))}
                    </Select>
                </Field>

                <BaseField shouldUpdate>
                    {({ }, { }, { getFieldValue }) => {
                        const type = getFieldValue("type");
                        const selectDisabled = disabled || !isMasterCompany;

                        switch (type) {
                            case "hour-type":
                                return (
                                    <Field
                                        name="hourTypes"
                                        label={t("hour-types")}
                                        rules={[
                                            {
                                                required: true,
                                                message: t("required-hour-type"),
                                            },
                                        ]}
                                    >
                                        <HourTypeSelect
                                            mode="multiple"
                                            optionLabelProp="value"
                                            placeholder={t("hour-types")}
                                            disabled={selectDisabled}
                                        />
                                    </Field>
                                );
                            case "earning-group":
                            case "groupings":
                            case "pay-code": {
                                const config = resourceSelectConfig[type];
                                if (!config?.resourcePath) {
                                    return null;
                                }

                                return (
                                    <Field
                                        name={config.field}
                                        label={t(config.label)}
                                        rules={[
                                            {
                                                required: true,
                                                message: t(config.requiredMessage),
                                            },
                                        ]}
                                    >
                                        <ResourceSelect
                                            key={type}
                                            mode="multiple"
                                            resourcePath={config.resourcePath}
                                            renderLabel={renderSettingsResourceLabel}
                                            placeholder={t(config.placeholder)}
                                            hasSearch
                                            getSearchFilters={getSettingsResourceSearchFilters}
                                            disabled={selectDisabled}
                                        />
                                    </Field>
                                );
                            }
                            default:
                                return null;
                        }
                    }}
                </BaseField>

                <ManageAbilitySelect
                    form={form}
                    disabled={disabled || !isMasterCompany}
                />

                <BaseField shouldUpdate>
                    {({ }, { }, { getFieldValue }) => {
                        const manageAbility = getFieldValue("manageAbility");

                        if (manageAbility !== "role") {
                            return null;
                        }

                        return (
                            <Field
                                name="roles"
                                label={t("roles")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("required-roles"),
                                    },
                                ]}
                            >
                                <ResourceSelect
                                    mode="multiple"
                                    labelPropName="description"
                                    resourcePath="/user-roles"
                                    placeholder={t("roles")}
                                    renderLabel={renderSettingsResourceLabel}
                                    hasSearch
                                    getSearchFilters={getSettingsResourceSearchFilters}
                                    disabled={disabled || !isMasterCompany}
                                />
                            </Field>
                        );
                    }}
                </BaseField>
            </div>

            <div className="d-flex justify-content-end">
                <Button
                    onClick={onClose}
                    disabled={loading}
                    className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
                >
                    {t("cancel")}
                </Button>

                {!disabled && isMasterCompany && (
                    <Button
                        type="submit"
                        className="btn-dark btn-sm shadow-none"
                        loading={loading}
                    >
                        {t("save")}
                    </Button>
                )}
            </div>
        </FormElement>
    );
}

export default Form;
